import { useEffect, useState } from 'react';
import { fetchConversationPagesByAccount } from '@/core/services/ConversationPagesService';
import userService from '@/core/services/UserService';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';
import { isMetricsChannel } from '../metricsMenuItems';
import { normalizeMetricsChannel } from '../metricsMenuPaths';
import type { PageOption } from '../components/MetricsPageSelector/MetricsPageSelector';

export interface MetricsMenuData {
  pages: PageOption[];
  isLoaded?: boolean;
  accountName?: string;
  universeId?: string;
}

const CHANNEL_DOMAINS: Record<string, string> = {
  facebook: 'https://facebook.com/',
  instagram: 'https://instagram.com/',
  tiktok: 'https://tiktok.com/',
  twitter: 'https://twitter.com/',
  youtube: 'https://youtube.com/',
  linkedin: 'https://linkedin.com/',
  threads: 'https://threads.com/',
};

const buildExternalUrl = (channel: string, username: string): string => {
  const domain = CHANNEL_DOMAINS[channel];
  return domain && username ? `${domain}${username}` : '';
};

const isLinkedToUniverse = (linked: Array<string | number> | undefined, universeId?: string): boolean =>
  !universeId || (linked?.some((id) => String(id) === universeId) ?? false);

const toNumber = (value: string): number => Number(value) || 0;

export const loadMetricsPages = async (): Promise<PageOption[]> => {
  const universeId = getUniId();
  const accountId = toNumber(getAccountId());
  const pages = await fetchConversationPagesByAccount();

  return pages
    .filter((page) => isMetricsChannel(normalizeMetricsChannel(page.channel)))
    .filter((page) => isLinkedToUniverse(page.linkedUniverses, universeId === 'DEFAULT' ? undefined : universeId))
    .map((page) => {
      const channel = normalizeMetricsChannel(page.channel);
      const username = page.username ?? '';
      return {
        id: page.id,
        title: page.title,
        username,
        profile_pic: '',
        channel,
        acc_id: accountId,
        externalUrl: buildExternalUrl(channel, username),
      };
    });
};

const loadAccountInfo = async (): Promise<Pick<MetricsMenuData, 'accountName'>> => {
  const accountId = getAccountId();
  const [user, account] = await Promise.all([
    userService.getUserMe().catch(() => undefined),
    accountId === 'DEFAULT' ? Promise.resolve(undefined) : userService.getAccountById(accountId).catch(() => undefined),
  ]);

  return { accountName: account?.name ?? user?.name };
};

/**
 * Loads everything the metrics menu needs by itself (pages, account info and universe),
 * so the consumer does not need to pass them as attributes.
 */
export const useMetricsMenuData = (enabled: boolean = true): MetricsMenuData => {
  const [data, setData] = useState<MetricsMenuData>({ pages: [], isLoaded: false });

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;
    const universeId = getUniId();

    Promise.all([loadMetricsPages().catch(() => []), loadAccountInfo()])
      .then(([pages, info]) => {
        if (!mounted) return;
        setData({ pages, isLoaded: true, ...info, universeId: universeId === 'DEFAULT' ? undefined : universeId });
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
    };
  }, [enabled]);

  return data;
};
